import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { fetchInvitationPreview, registerByInvitation } from '../../api/invitations';
import { queryKeys } from '../../api/queryKeys';
import { Button } from '../../components/Button';
import { Input } from '../../components/Input';
import { Logo } from '../../components/Logo';
import { Skeleton } from '../../components/Skeleton';
import { humanizeError } from '../../lib/errors';
import styles from './RegisterPage.module.css';

/**
 * UC-AUTH-09: регистрация по приглашению. Страница публичная, токен берётся
 * из `?token=…`; пароль задаёт сам пользователь, после чего он сразу входит.
 */
export function RegisterPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const token = searchParams.get('token') ?? '';
  const [form, setForm] = useState({ password: '', repeat: '' });
  const [error, setError] = useState<string | null>(null);

  const previewQuery = useQuery({
    queryKey: ['invitation-preview', token],
    queryFn: ({ signal }) => fetchInvitationPreview(token, signal),
    enabled: token.length > 0,
    retry: false,
  });

  const mutation = useMutation({
    mutationFn: () => registerByInvitation({ token, password: form.password }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.me });
      navigate('/', { replace: true });
    },
    onError: (cause: unknown) => setError(humanizeError(cause).message),
  });

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    setError(null);
    if (form.password.length < 8) {
      setError('Пароль не короче 8 символов');
      return;
    }
    if (form.password !== form.repeat) {
      setError('Пароли не совпадают');
      return;
    }
    mutation.mutate();
  };

  // Ссылка без токена или с просроченным токеном — дальше идти некуда.
  const isInvalid = token.length === 0 || previewQuery.isError;

  return (
    <main className={styles['screen']}>
      <form className={styles['card']} onSubmit={handleSubmit} noValidate>
        <div className={styles['brand']}>
          <Logo size="lg" />
          <h1 className={styles['title']}>Регистрация</h1>
          <p className={styles['subtitle']}>Задайте пароль, чтобы начать работу</p>
        </div>

        {previewQuery.isPending && token.length > 0 ? <Skeleton variant="row" count={2} /> : null}

        {isInvalid ? (
          <div className={styles['error']} role="alert">
            <p>
              {token.length === 0
                ? 'В ссылке нет токена приглашения.'
                : humanizeError(previewQuery.error).message}
            </p>
            <p>
              Запросите новое приглашение у администратора или <Link to="/login">войдите</Link>.
            </p>
          </div>
        ) : null}

        {previewQuery.data ? (
          <>
            <p className={styles['subtitle']}>
              Приглашение для <span className={styles['email']}>{previewQuery.data.email}</span>
            </p>

            <Input
              label="Новый пароль"
              name="password"
              type="password"
              autoComplete="new-password"
              autoFocus
              value={form.password}
              hint="Не короче 8 символов"
              onChange={(event) => setForm((prev) => ({ ...prev, password: event.target.value }))}
            />
            <Input
              label="Повторите пароль"
              name="repeat"
              type="password"
              autoComplete="new-password"
              value={form.repeat}
              onChange={(event) => setForm((prev) => ({ ...prev, repeat: event.target.value }))}
            />

            {error ? (
              <div className={styles['error']} role="alert">
                <p>{error}</p>
              </div>
            ) : null}

            <Button type="submit" variant="primary" fullWidth loading={mutation.isPending}>
              Зарегистрироваться
            </Button>
          </>
        ) : null}
      </form>
    </main>
  );
}
